// ----------------------------------------------------------------------
// <copyright file="Izenda.QuerySourceTypeStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  var QuerySourceType = require('./data/Izenda.QuerySourceTypeConfig').QUERY_SOURCE_TYPE;
  var uiState = require('logic/Izenda.UiState');

  var BUNDLE = require('./data/Izenda.QuerySourceTypeConfig').BUNDLE;

  var m = require('mobx');
  var action = require('mobx').action;
  var observable = require('mobx').observable;
  var concatArray = require('share/utils/Izenda.LazyLoadingUtils').concatArray;
  var fetcher = require('fetchers/Izenda.DataModelDataFetcher');

  var CategoryGridStore = require('./Izenda.CategoryGridStore');
  var QuerySourceTypeDetailsStore = require('./Izenda.QuerySourceTypeDetailsStore');
  var Paging = require('share/components/controls/DataGrid/helpers/Izenda.PagingModel');
  var CommonMessages = require('share/messages/Izenda.CommonMessages');

  var ExpressionSelection = require('logic/models/Izenda.ExpressionSelection');
  var ExpressionFieldName = require('logic/models/Izenda.ExpressionFieldName');
  var ExpressionFunctionOperator = require('logic/models/Izenda.ExpressionFunctionOperator');
  const { isEmpty, some, map, isNil, omit, flatMap, findIndex, each, clone, find } = require('lodash/fp');

  function QuerySourceTypeStore() {
    // important - query source will decide which columns will be show
    this.hasDynamicFeature = false;

    m.extendObservable(this, {
      querySourceType: '',
      relationship: null,
      categoryList: new CategoryGridStore(),
      querySourceList: [],
      selectedQuerySource: null,
      isLoading: true,
      totalRecords: 0,
      pageInfo: new Paging(),
      hasChange: function() {
        return some(['isDirty', true], this.querySourceList) || some(['hasDetailChange', true], this.querySourceList);
      },
      canSave: function() {
        var selectedFieldsList = [];
        if (this.selectedQuerySource) {
          var selectedQuerySourceId = this.selectedQuerySource.getId();
          var index = findIndex(function(item) {
            return item.getId() === selectedQuerySourceId;
          }, this.querySourceList);
          selectedFieldsList = this.querySourceList[index].getFieldLists();
        }

        return (
          this.hasChange ||
          some('details.physicalChange', this.querySourceList) ||
          some('details.allowToSave', selectedFieldsList)
        );
      },
      getCriteriaList: function() {
        var CriteriaCollection = BUNDLE.CRITERIALIST;
        return CriteriaCollection[this.querySourceType] || CriteriaCollection.DEFAULT_CRITERIA_LIST;
      },
      showPopup: false,
      alertMessage: CommonMessages.EMPTY_MESSAGE
    });
  }

  /**
   * init for query source type, call when QuerySourceTypeStore mount
   * @return {[type]} [description]
   */
  QuerySourceTypeStore.prototype.init = function() {
    this.alertMessage = CommonMessages.EMPTY_MESSAGE;
    this.observeTenantId();
    uiState.setCheckDirtyFunction(
      function() {
        return this.hasChange;
      }.bind(this)
    );
  };

  QuerySourceTypeStore.prototype.destroy = m.action(function() {
    this.disposeQuerySourceType();
    uiState.resetCheckDirtyFunction();
  });

  /**
   * observe tenantID on setting level
   */
  QuerySourceTypeStore.prototype.observeTenantId = function() {
    this.disposeQuerySourceType = m.observe(
      uiState,
      'tenantId',
      function() {
        this.goToPage(1);
      }.bind(this)
    );
  };

  /**
   * Get init data (list of stored procedure)
   */
  QuerySourceTypeStore.prototype.setQuerySourceType = m.action(function(querySourceType) {
    this.querySourceType = querySourceType;
    // Get categories list
    // this.categoryList.getCategories();
    // important - must be reset all data
    this.resetData();
    this.resetPagingInfo();
    // get data based on new query-source-type
    return this.loadQuerySources();
  });

  /**
   *Search data
   */
  QuerySourceTypeStore.prototype.search = m.action(function(searchProperty, searchText) {
    this.resetData();
    this.pageInfo.setSearchValues(searchProperty, searchText);
    return this.goToPage(1);
  });

  /**
   * Reload data when save fail or cancel
   */
  QuerySourceTypeStore.prototype.reload = m.action(function() {
    this.resetData();
    this.loadQuerySources();
  });

  /**
   * Get init data (list of stored procedure)
   */
  QuerySourceTypeStore.prototype.resetData = m.action(function() {
    // TODO should use other way
    this.querySourceList = [];
    this.selectedQuerySource = null;
    this.isLoading = true;
    this.totalRecords = 0;
  });

  QuerySourceTypeStore.prototype.resetPagingInfo = m.action(function() {
    // reset paging information
    var DefaultSortCollection = BUNDLE.SORTDEFAULTLIST;
    var colSortDir = clone(DefaultSortCollection[this.querySourceType] || DefaultSortCollection.DEFAULT_SORT);
    this.pageInfo = new Paging(colSortDir, BUNDLE.DEFAULT_CRITERIA);
  });

  /**
   * Get init data (list of stored procedure)
   */
  QuerySourceTypeStore.prototype.loadQuerySources = m.action(function() {
    var pageInfo = {
      pageSize: this.pageInfo.pageSize,
      pageIndex: this.pageInfo.pageIndex
    };
    var tenantId = uiState.getTenantId() || null;
    this.selectedQuerySource = null;

    // TODO for sort
    this.pageInfo.buildPagingRequest();
    var sortOrders = this.pageInfo.sortOrders;

    // re-load datasource category before load querysource
    // because the categories group have new item already added but never updated
    return Promise.join(
      this.categoryList.getCategories(tenantId),
      fetcher.loadQuerySources(tenantId, this.querySourceType, pageInfo, sortOrders, this.pageInfo.searchCriteria),
      function(categories, querySources) {
        return this.populateData(querySources);
      }.bind(this)
    );
  });

  /**
   * Go to page
   * @param {number} pageIndex
   * @returns
   */
  QuerySourceTypeStore.prototype.goToPage = m.action(function(pageIndex) {
    var pageInfo = this.pageInfo;

    pageInfo.setPageIndex(pageIndex);

    return this.loadQuerySources();
  });

  /**
   * Create list of query source details
   * @param {array} spDetails
   */
  QuerySourceTypeStore.prototype.populateData = m.action(function(querySourceDetails) {
    querySourceDetails = querySourceDetails || {};
    var querySourceList = querySourceDetails.result;

    this.totalRecords = querySourceDetails.total;

    // create list of QuerySourceTypeDetailsVM
    var newData = map(function(querySource) {
      return new QuerySourceTypeDetailsStore(querySource);
    }, querySourceList);

    // update dynamic feature based on data of query-source
    querySourceList.length > 0 && (this.hasDynamicFeature = hasDynamicFeature(querySourceList[0]));

    // update page info
    var pageInfo = this.pageInfo;
    pageInfo.totalRecords = querySourceDetails.total;

    // update data to UI
    m.transaction(
      function() {
        // update to model
        this.querySourceList = newData;

        // set first item as showing item
        newData.length > 0 &&
          this.querySourceType !== QuerySourceType.FUNCTION &&
          this.setShowingQuerySource(newData[0]);
      }.bind(this)
    );

    function hasDynamicFeature(querySource) {
      return querySource && querySource.extendedProperties && querySource.extendedProperties.Dynamic;
    }

    return null;
  });

  QuerySourceTypeStore.prototype.setShowingQuerySource = m.action(function(selectedQuerySource) {
    // select the same item: do nothing
    if (this.selectedQuerySource && this.selectedQuerySource.getId() === selectedQuerySource.getId()) {
      return;
    }

    // mark the previous store proc as unselected
    if (this.selectedQuerySource) {
      this.selectedQuerySource.isSelected = false;
      this.selectedQuerySource.fieldList = [];
    }

    // update new selected query-source
    this.selectedQuerySource = selectedQuerySource;
    selectedQuerySource.isSelected = true;

    var querySourceList = this.querySourceList;
    var selectedQuerySourceDetail = selectedQuerySource && selectedQuerySource.details;
    var selectedQuerySourceId = selectedQuerySourceDetail && selectedQuerySourceDetail.id;

    return Promise.join(
      // get schema details
      selectedQuerySource.loadQuerySourceFields(),
      // get funtion/operators
      fetcher.fetchFunctionOperators(uiState.getTenantId(), selectedQuerySourceId, true),
      function(querySourceFields, functionOperators) {
        var querySourceListDetails = flatMap(function(item) {
          return item.details;
        }, querySourceList);

        m.transaction(function() {
          selectedQuerySource.calculatedStore.expressionSelection = new ExpressionSelection({
            expressionFieldName: new ExpressionFieldName({
              selectedDataSource: selectedQuerySourceDetail,
              dataSources: querySourceListDetails
            }),
            expressionFunctionOperators: new ExpressionFunctionOperator({
              functionOperators: functionOperators
            })
          });

          selectedQuerySource.loadFieldNameForExpression();
        });
        return null;
      }
    );
  });

  QuerySourceTypeStore.prototype.fetchAllQuerySourceList = function() {
    return fetcher.loadAllQuerySources(uiState.getTenantId(), this.querySourceType);
  };

  QuerySourceTypeStore.prototype.getBundle = function() {
    return BUNDLE;
  };

  QuerySourceTypeStore.prototype.isFieldInvisibleOrUnfilterable = function() {
    if (isNil(this.selectedQuerySource)) return;
    var selectedQuerySourceId = this.selectedQuerySource.getId();

    var index = findIndex(function(item) {
      return item.getId() === selectedQuerySourceId;
    }, this.querySourceList);

    var fieldList = this.querySourceList[index].getFieldLists();
    var itemChanged = find(function(o) {
      return o.isChangeToInvisible == true || o.isChangeToUnfilterable == true;
    }, fieldList);

    return !isEmpty(itemChanged);
  };

  QuerySourceTypeStore.prototype.updateQuerySource = m.action(function() {
    var buildQuerySources = function() {
      var result = [];

      each(
        function(item) {
          var originDetails = item.getDetails();
          var details = omit(['extendedProperties', 'querySourceFields'], originDetails);
          details.type = this.querySourceType;
          details.extendedProperties = JSON.stringify(originDetails.extendedProperties);
          details.querySourceFields = [];
          // Set id = null when add new category
          details.dataSourceCategoryId = details.isNewCategory ? null : details.dataSourceCategoryId;
          result.push(details);
        }.bind(this),
        this.querySourceList
      );

      return result;
    }.bind(this);

    var buildQuerySourceFields = function(querySources) {
      if (this.selectedQuerySource) {
        var selectedQuerySourceId = this.selectedQuerySource.getId();

        var index = findIndex(function(item) {
          return item.getId() === selectedQuerySourceId;
        }, this.querySourceList);

        var fieldLists = this.querySourceList[index].getFieldLists();
        each(function(item) {
          var fieldDetails = item.getDetails() || {};
          var details = omit('filteredValue', fieldDetails);
          var filteredValue = fieldDetails.filteredValue;
          details.filteredValue = JSON.stringify(filteredValue);
          querySources[index].querySourceFields.push(details);
        }, fieldLists);
      }
    }.bind(this);

    var buildUpdatingQuerySourceData = function() {
      var result = {};

      result.tenantId = uiState.getTenantId();
      result.querySources = buildQuerySources();
      buildQuerySourceFields(result.querySources);

      return result;
    }.bind(this);

    var querySourceData = buildUpdatingQuerySourceData();
    return fetcher.updateQuerySource(querySourceData);
  });

  QuerySourceTypeStore.prototype.deleteCustomQuerySource = function(item) {
    return fetcher.deleteCustomQuerySource({ id: item.details.id });
  };

  QuerySourceTypeStore.prototype.getDatabases = action(function(customView, pageInfo, isReset) {
    var data = {
      tenantId: uiState.getTenantId(),
      skipItems: pageInfo.fromIndex,
      pageSize: pageInfo.pageSize
    };

    return fetcher.getDatabases(data).then(
      action(response => {
        customView.isLastPageDatabase = response.isLastPage;
        isReset && (customView.databases = []);
        const databases = map(item => observable(item), response.result);

        return concatArray(customView.databases, databases, 'key', !response.isLastPage);
      })
    );
  });

  QuerySourceTypeStore.prototype.getSchemas = action(function(customView, pageInfo, isReset) {
    var data = {
      connectionId: customView.databaseId,
      skipItems: pageInfo.fromIndex,
      pageSize: pageInfo.pageSize
    };

    return fetcher.getSchemas(data).then(
      action(response => {
        customView.isLastPageSchema = response.isLastPage;
        isReset && (customView.schemas = []);
        const schemas = map(item => observable(item), response.result);

        return concatArray(customView.schemas, schemas, 'key', !response.isLastPage);
      })
    );
  });

  QuerySourceTypeStore.prototype.validateCustomQuerySource = function(customView) {
    const data = {
      id: customView.id,
      name: customView.viewName,
      connectionId: customView.databaseId,
      categoryId: customView.schemaId,
      customDefinition: customView.query
    };

    return fetcher.validateCustomQuerySource(data);
  };

  QuerySourceTypeStore.prototype.saveCustomQuerySource = function(customView) {
    const data = {
      id: customView.id,
      name: customView.viewName,
      connectionId: customView.databaseId,
      categoryId: customView.schemaId,
      customDefinition: customView.query
    };

    return fetcher.saveCustomQuerySource(data);
  };

  module.exports = QuerySourceTypeStore;
})();



// WEBPACK FOOTER //
// ./ui/logic/Izenda.QuerySourceTypeStore.js